import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Customer360Service } from 'src/app/private/services/customer-360.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { MODULE_LOADING_MESSAGES } from 'src/app/private/services/module.config';

const MAX_FILE_BYTES = 4 * 1024 * 1024;

@Component({
  selector: 'app-lead-profile-documents',
  templateUrl: './lead-profile-documents.component.html',
  styleUrls: ['./lead-profile-documents.component.scss'],
})
export class LeadProfileDocumentsComponent implements OnChanges, OnDestroy {
  @Input() leadId: number | string | null = null;
  @Input() refreshToken = 0;
  @Output() changed = new EventEmitter<void>();

  documents: any[] = [];
  
  private _loading = false;
  get loading(): boolean {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    if (val) {
      this.startDocumentsMessageRotation();
    } else {
      this.cleanupDocumentsMessageRotation();
    }
  }

  uploading = false;

  documentsActiveMessages: string[] = [];
  documentsCurrentMessageIndex = 0;
  documentsCurrentMessage = '';
  documentsFadeState: 'fade-out' | 'fade-in' | '' = '';
  private documentsMessageInterval: any = null;

  constructor(
    private customer360: Customer360Service,
    private localStorage: LocalStorageService,
    private toast: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] || changes['refreshToken']) {
      this.load();
    }
  }

  load(): void {
    if (this.leadId == null || this.leadId === '') {
      return;
    }
    this.loading = true;
    this.customer360.getCustomerDocuments(this.leadId).subscribe({
      next: (res: any) => {
        this.documents = res?.rows ?? [];
        this.loading = false;
      },
      error: () => {
        this.documents = [];
        this.loading = false;
      },
    });
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  async onFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.leadId == null) {
      return;
    }
    input.value = '';

    if (file.size > MAX_FILE_BYTES) {
      this.toast.error('Document must be 4 MB or smaller.');
      return;
    }

    this.uploading = true;
    try {
      const fileBase64 = await this.readFileAsBase64(file);
      this.customer360
        .createCustomerDocument(this.leadId, {
          document_name: file.name,
          file_base64: fileBase64,
          file_type: file.type || undefined,
          file_size: file.size,
          uploaded_by: Number(this.localStorage.getUserId()) || undefined,
        })
        .subscribe({
          next: () => {
            this.uploading = false;
            this.toast.success('Document saved');
            this.load();
            this.changed.emit();
          },
          error: (err: any) => {
            this.uploading = false;
            const message =
              err?.error?.message ||
              err?.error?.error ||
              'Could not upload document';
            this.toast.error(message);
          },
        });
    } catch {
      this.uploading = false;
      this.toast.error('Could not read file');
    }
  }

  ngOnDestroy(): void {
    this.cleanupDocumentsMessageRotation();
  }

  private startDocumentsMessageRotation() {
    if (this.documentsMessageInterval) return;
    this.documentsActiveMessages = MODULE_LOADING_MESSAGES['leads'] || MODULE_LOADING_MESSAGES['generic'] || ['Loading...'];
    this.documentsCurrentMessageIndex = 0;
    this.documentsCurrentMessage = this.documentsActiveMessages[this.documentsCurrentMessageIndex] || 'Loading...';
    this.documentsFadeState = '';

    this.documentsMessageInterval = setInterval(() => {
      this.documentsFadeState = 'fade-out';
      setTimeout(() => {
        if (!this.loading) {
          this.cleanupDocumentsMessageRotation();
          return;
        }
        this.documentsCurrentMessageIndex = (this.documentsCurrentMessageIndex + 1) % this.documentsActiveMessages.length;
        this.documentsCurrentMessage = this.documentsActiveMessages[this.documentsCurrentMessageIndex];
        this.documentsFadeState = 'fade-in';
        setTimeout(() => {
          this.documentsFadeState = '';
        }, 200);
      }, 200);
    }, 2000);
  }

  private cleanupDocumentsMessageRotation() {
    if (this.documentsMessageInterval) {
      clearInterval(this.documentsMessageInterval);
      this.documentsMessageInterval = null;
    }
  }
}
